import React, { useState, useEffect } from 'react';
import './CookieBanner.css';

const CONSENT_KEY = 'prohub_cookie_consent';

type ConsentState = 'accepted' | 'essential-only' | null;

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY) as ConsentState;
    if (!stored) setVisible(true);
  }, []);

  const accept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);
  };

  const declineOptional = () => {
    localStorage.setItem(CONSENT_KEY, 'essential-only');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <div className="cookie-banner__inner">
        <div className="cookie-banner__main">
          <div className="cookie-banner__text">
            <span className="cookie-banner__title">🍪 Cookies &amp; Data</span>
            <p className="cookie-banner__summary">
              We use only functional cookies — no tracking, no analytics, no third parties.
              {' '}
              <button className="cookie-banner__details-toggle" onClick={() => setExpanded(v => !v)}>
                {expanded ? 'Hide details ▲' : 'What do we store? ▼'}
              </button>
            </p>

            {expanded && (
              <div className="cookie-banner__details">
                <table className="cookie-banner__table">
                  <thead>
                    <tr>
                      <th>What</th>
                      <th>Why</th>
                      <th>Third party?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Session token (cookie)</td>
                      <td>Keeps you logged in</td>
                      <td className="cookie-banner__no">No</td>
                    </tr>
                    <tr>
                      <td>Saved teams &amp; settings (localStorage)</td>
                      <td>Remembers your team builder data &amp; preferences</td>
                      <td className="cookie-banner__no">No</td>
                    </tr>
                    <tr>
                      <td>Cookie consent choice (localStorage)</td>
                      <td>Stores your choice so this banner doesn't reappear</td>
                      <td className="cookie-banner__no">No</td>
                    </tr>
                  </tbody>
                </table>
                <p className="cookie-banner__note">
                  All data stays in your browser or on our own servers. We never sell or share your data.
                  Session tokens expire when you log out or your session ends.
                </p>
              </div>
            )}
          </div>

          <div className="cookie-banner__actions">
            <button className="cookie-banner__btn cookie-banner__btn--accept" onClick={accept}>
              Accept all
            </button>
            <button className="cookie-banner__btn cookie-banner__btn--decline" onClick={declineOptional}>
              Essential only
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Returns the stored consent value, or null if no choice made yet. */
export const getCookieConsent = (): ConsentState =>
  (localStorage.getItem(CONSENT_KEY) as ConsentState) ?? null;

export default CookieBanner;
